import XLSX from "xlsx-js-style";

const border = {
  top: { style: "thin", color: { rgb: "FFBFBFBF" } },
  right: { style: "thin", color: { rgb: "FFBFBFBF" } },
  bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
  left: { style: "thin", color: { rgb: "FFBFBFBF" } },
};

const sHeader = {
  font: { bold: true, sz: 12 },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  fill: { patternType: "solid", fgColor: { rgb: "FFF2F2F2" } },
  border,
};

const baseCell = {
  alignment: { vertical: "center", wrapText: true },
  border,
};

const numCell = {
  ...baseCell,
  alignment: { horizontal: "right", vertical: "center", wrapText: true },
};

const fill = (rgb) => ({
  fill: { patternType: "solid", fgColor: { rgb } },
});

const ddmmyyyy = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const calcStatus = (nextVerificationDate) => {
  if (!nextVerificationDate) return { status: "Не задано", color: "default" };
  const d =
    nextVerificationDate instanceof Date
      ? nextVerificationDate
      : new Date(nextVerificationDate);
  if (Number.isNaN(d.getTime())) return { status: "Неверная дата", color: "default" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { status: "Просрочено", color: "error" };

  const monthsDiff = diffDays / 30.44;
  if (monthsDiff >= 3) return { status: "Норма", color: "success" };
  if (monthsDiff >= 2) return { status: "Внимание", color: "warning" };
  return { status: "Срочно", color: "error" };
};

const statusFill = (statusColor) => {
  switch (statusColor) {
    case "success":
      return fill("FFE6F4EA"); // light green
    case "warning":
      return fill("FFFFF4E5"); // light yellow
    case "error":
      return fill("FFFDE7E9"); // light red
    default:
      return fill("FFF5F5F5"); // light gray
  }
};

const autoWidth = (ws) => {
  const ref = ws["!ref"];
  if (!ref) return;
  const range = XLSX.utils.decode_range(ref);
  const widths = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let max = 10;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      const text = cell?.v == null ? "" : String(cell.v);
      const len = Math.min(80, Math.ceil(text.length * 1.1) + 2);
      if (len > max) max = len;
    }
    widths.push(max);
  }
  ws["!cols"] = widths.map((wch) => ({ wch }));
};

export function exportDeviceIndicatorExcel(rows, ctx = {}) {
  const columns = [
    { k: "id", t: "ID" },
    { k: "filial", t: "Филиал" },
    { k: "filialDivision", t: "Районное отделение" },
    { k: "department", t: "Лаборатория" },
    { k: "name", t: "Наименование" },
    { k: "type", t: "Тип" },
    { k: "serialNumber", t: "Заводской номер" },
    { k: "manufactureYear", t: "Год выпуска" },
    { k: "quantity", t: "Количество" },
    { k: "rankAccuracyError", t: "Разряд/класс/погрешность" },
    { k: "measurementRange", t: "Диапазон измерения" },
    { k: "note", t: "Примечание" },
    { k: "lastVerificationDate", t: "Дата последней поверки" },
    { k: "nextVerificationDate", t: "Дата последующей поверки" },
    { k: "periodicity", t: "Периодичность" },
    { k: "status", t: "Статус" },
    { k: "isIrrelevant", t: "Неактуальный" },
  ];

  const aoa = [];
  aoa.push(columns.map((c) => ({ v: c.t, s: sHeader })));

  (rows || []).forEach((r) => {
    const s = calcStatus(r?.nextVerificationDate);
    const rowStyle = statusFill(s.color);
    const cellStyle = { ...baseCell, ...rowStyle };
    const cellStyleNum = { ...numCell, ...rowStyle };

    const rowObj = {
      id: r?.id ?? "",
      filial: r?.filial?.name ?? "",
      filialDivision: r?.filialDivision?.name ?? "",
      department: r?.department?.name ?? "",
      name: r?.name ?? "",
      type: r?.type ?? "",
      serialNumber: r?.serialNumber ?? "",
      manufactureYear: r?.manufactureYear ?? "",
      quantity: Number.isFinite(Number(r?.quantity)) ? Number(r.quantity) : 1,
      rankAccuracyError: r?.rankAccuracyError ?? "",
      measurementRange: r?.measurementRange ?? "",
      periodicity: r?.periodicity ?? "",
      note: r?.note ?? "",
      lastVerificationDate: ddmmyyyy(r?.lastVerificationDate),
      nextVerificationDate: ddmmyyyy(r?.nextVerificationDate),
      status: s.status,
      isIrrelevant: r?.isIrrelevant ? "Да" : "Нет",
    };

    aoa.push(
      columns.map((c) => {
        const isQuantity = c.k === "quantity";
        return {
          v: rowObj[c.k],
          s: isQuantity ? cellStyleNum : cellStyle,
        };
      })
    );
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!rows"] = [{ hpt: 30 }];
  autoWidth(ws);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Отчет");

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const name =
    ctx?.fileName ||
    `Индикатор_поверки_оборудования_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

